<template>
    <div>
        <input
            v-if="method === 'post' && savedImage"
            :value="savedImage"
            type="hidden"
            :name="name"
        />

        <div class="mx-4 flex flex-col" @click.stop="handleClick">
            <slot
                :delete-url="deleteUrl"
                :delete-image="deleteImage"
                :image="image"
                :image-url="imageUrl"
            ></slot>
        </div>

        <div class="hidden">
            <canvas
                ref="resizedCanvas"
                id="resized"
                class="border"
                width="400"
                height="400"
            ></canvas>
        </div>

        <div
            v-show="cropper"
            class="modal flex justify-center items-center pb-32"
        >
            <input
                class="hidden"
                type="file"
                ref="input"
                @change="handleChange"
                accept="image/*"
            />

            <div class="image-upload-container" :class="{ circle: circular }">
                <img
                    class="max-w-full hidden"
                    src=""
                    ref="img"
                    alt="Cropped Image"
                />

                <div v-if="!saving" class="flex mt-3">
                    <button
                        type="button"
                        class="w-1/2 bg-grey-light hover:bg-grey-dark text-grey-darkest text-sm font-semibold py-2 px-4 rounded mr-2"
                        @click="cleanUp"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        class="w-1/2 bg-teal hover:bg-teal-dark text-white text-sm font-semibold py-2 px-4 rounded ml-2"
                        @click="save"
                    >
                        Save
                    </button>
                </div>

                <div v-else class="flex justify-center items-center mt-1">
                    <div class="spinner mr-3"></div>

                    <p class="text-lg pb-3 pr-1 pt-4">
                        Uploading your picture...
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Cropper from 'cropperjs';
import Pica from 'pica';

const pica = Pica();
const MAX_UPLOAD_MB = 6;

export default {
    props: {
        circular: Boolean,
        image: String,
        imageRoot: {
            type: String,
            required: false,
        },
        saveUrl: {
            type: String,
            required: true,
        },
        deleteUrl: {
            type: String,
            required: false,
        },
        method: {
            type: String,
            required: true,
            validator: (value) => {
                return ['post', 'patch'].indexOf(value) !== -1;
            },
        },
        name: {
            type: String,
            default: 'avatar',
        },
    },
    data() {
        return {
            cropper: null,
            savedImage: this.image,
            saving: false,
        };
    },
    computed: {
        imageUrl() {
            return this.savedImage ? this.imageRoot + this.savedImage : '';
        },
    },
    methods: {
        handleClick(event) {
            event.target.blur();
            this.$refs.input.value = '';
            this.$refs.input.click();
        },
        handleChange() {
            if (this.$refs.input.files.length) {
                if (
                    this.$refs.input.files[0].size / 1024 / 1024 >
                    MAX_UPLOAD_MB
                ) {
                    alert(
                        'This file is too large. Please select an image under ' +
                            MAX_UPLOAD_MB +
                            'MB.'
                    );
                    this.$refs.img.src = null;
                } else {
                    this.$refs.img.src = URL.createObjectURL(
                        this.$refs.input.files[0]
                    );
                    this.cropper = new Cropper(this.$refs.img, {
                        aspectRatio: 1,
                        viewMode: 2,
                    });
                }
            }
        },
        send(canvas, callback) {
            pica.resize(canvas, this.$refs.resizedCanvas)
                .then((result) => pica.toBlob(result, 'image/jpeg', 0.9))
                .then((blob) => {
                    let data = new FormData();
                    data.append('_method', this.method);
                    data.append(this.name, blob);

                    window.axios
                        .request({
                            url: this.saveUrl,
                            method: 'post',
                            data: data,
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        })
                        .then(callback);
                });
        },
        save() {
            this.saving = true;
            this.cropper.disable();
            let canvas = this.cropper.getCroppedCanvas();

            this.send(canvas, (response) => {
                this.saving = false;
                this.cropper.enable();
                this.savedImage = response.data;
                this.cleanUp();
            });
        },
        cleanUp() {
            this.cropper.destroy();
            this.cropper = null;
        },
        deleteImage() {
            if (this.deleteUrl) {
                window.axios.post(this.deleteUrl).then(() => {
                    window.location.reload();
                });
            } else {
                this.savedImage = null;
            }
        },
    },
};
</script>

<style>
.image-upload-container {
    width: 300px;
    height: 300px;
}
.circle .cropper-view-box {
    border-radius: 50%;
}
</style>
